import { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { lockScroll } from '@/components/ui';
import { isNative } from '@/native';

/**
 * 图纸 / 进度表的全屏查看外壳。两端共用（CR-026）。
 *
 * **客户原话**：「手机和电脑查看图纸时候点击图片开始全屏查看不要在网页窗口内。
 * 像 adobe 这些软件那样。」
 *
 * 做两件事，缺一不可：
 *
 * 1. **一层铺满视口的遮罩**。这是真正保证「看图时屏幕上只有图」的那一层 ——
 *    它在任何浏览器、任何 WebView 里都成立，不依赖任何 API。
 * 2. **顺带请求一次浏览器全屏**（Fullscreen API）。这一层只多做一件事：
 *    把浏览器自己的地址栏和标签页也收掉。拿不到就算了，遮罩照样是满屏。
 *
 * 顺序很重要：遮罩是主力，全屏是加分项。反过来写（拿不到全屏就不显示）
 * 会让 iOS Safari 和装了策略的浏览器直接看不了图。
 */

/** 老 WebKit 的前缀版本。Chrome / Edge / Firefox 都走标准名，这里只是兜底。 */
interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

function currentFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function FullscreenViewer({
  title,
  meta,
  actions,
  aside,
  allowEscapeClose = true,
  onClose,
  children,
}: {
  title: string;
  /** 副标题：版本、分类这些。放标题下面一行 */
  meta?: ReactNode;
  /** 标题栏右侧的自定义按钮（管理端的「加图钉」） */
  actions?: ReactNode;
  /** 图纸下方的图钉清单 / 详情。浅色底，和深色的看图区分开 */
  aside?: ReactNode;
  /**
   * 上面还压着别的弹层（比如加图钉的抽屉）时传 false：
   * ESC 该先关那一层，不能把整个查看器一起带走，半张填好的表单就没了。
   */
  allowEscapeClose?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  /** 真的进过全屏才认「退出全屏 = 想关掉」。见下面 Capacitor 那段注释 */
  const enteredRef = useRef(false);

  // onClose 通常是行内箭头函数，每次渲染都是新的。放进 ref 让下面几个
  // 监听器只装一次 —— 反复装拆 fullscreenchange 容易和浏览器的状态赛跑。
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const escapeRef = useRef(allowEscapeClose);
  escapeRef.current = allowEscapeClose;

  /**
   * 进全屏。用 layout effect 而不是普通 effect：它和触发打开的那次点击
   * 在同一个任务里跑完，浏览器的「用户手势」还没过期 —— 晚一拍请求会被直接拒掉。
   */
  useLayoutEffect(() => {
    /**
     * **原生壳里不请求全屏**。Capacitor 的 `BridgeWebChromeClient.onShowCustomView()`
     * 第一行就是 `callback.onCustomViewHidden()` —— WebView 刚进全屏就被当场撤销，
     * 我们会立刻收到一次「已退出全屏」，把刚打开的查看器关掉，
     * 工人看到的是「点一下图纸闪一下又回来了」。
     *
     * 而 APK 里本来就没有地址栏和标签页，遮罩已经是整屏，全屏没有任何可赚的。
     */
    if (isNative) return;

    const el = rootRef.current as FullscreenElement | null;
    if (!el) return;

    const request = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    // iPhone 上的 Safari 没有元素级全屏（只有 <video> 有）。没有就不做，遮罩顶上。
    if (!request) return;

    let cancelled = false;
    void Promise.resolve()
      .then(() => request())
      .then(
        () => {
          if (!cancelled) enteredRef.current = true;
        },
        () => {
          // 手势过期、被 permissions policy 禁掉、用户拒绝 —— 都不是错误，
          // 遮罩本来就能看图。吞掉它，不要弹任何东西打断工人。
        },
      );

    return () => {
      cancelled = true;
      if (currentFullscreenElement() !== el) return;
      const doc = document as FullscreenDocument;
      const exit = doc.exitFullscreen?.bind(doc) ?? doc.webkitExitFullscreen?.bind(doc);
      void Promise.resolve()
        .then(() => exit?.())
        .catch(() => undefined);
    };
  }, []);

  /** 退出全屏（ESC / 系统手势 / 按了浏览器自己的退出键）＝ 关掉查看器 */
  useEffect(() => {
    const onChange = () => {
      if (currentFullscreenElement() === rootRef.current) {
        enteredRef.current = true;
        return;
      }
      if (!enteredRef.current) return;
      if (!escapeRef.current) return;
      closeRef.current();
    };

    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  /**
   * 没进全屏时 ESC 也要能关。
   *
   * 进了全屏的话这个监听器收不到 ESC —— 浏览器自己把它吃掉去退出全屏了，
   * 上面那条 fullscreenchange 才是那种情况下的出口。
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!escapeRef.current) return;
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // 背后的列表不能跟着滚：全屏退出以后回到的应该还是原来那个位置
  useEffect(() => lockScroll(), []);

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // bg-ink 是最深的那档正文色。看图要深底：浅底会让人误判图纸本身的灰度，
      // 而工地上的图纸经常是扫描件，底色本来就不白。
      className="fixed inset-0 z-sheet flex flex-col bg-ink"
    >
      {/*
        标题栏压到最薄（CR-030）。客户原话：「手机全屏打开 pdf 查看后，
        顶部空间浪费太多，手机屏幕本来就小，这样看图不方便了。」

        这一条的高度由关闭按钮决定，所以它从 56px 降到 44px —— **仍然是
        无障碍下限**，破的只是「工人端一律 56px」那条。它是全屏里唯一的出口，
        不能再小；真正省下来的大头在查看器那边（整行工具栏改成浮层）。
      */}
      <header className="safe-top flex shrink-0 items-center justify-between gap-3 px-3 py-1 text-surface">
        <div className="min-w-0 flex-1">
          <p className="truncate text-title font-semibold leading-snug">{title}</p>
          {meta && <p className="truncate text-caption leading-tight opacity-75">{meta}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('app.close')}
            className="flex min-h-touch items-center justify-center rounded-md px-3 text-body font-medium text-surface hover:bg-white/10"
          >
            {t('app.close')}
          </button>
        </div>
      </header>

      {/* 手机上图纸区贴边：省下的 24px 宽度直接变成图上的像素 */}
      <div className="min-h-0 flex-1 overflow-hidden pb-2 sm:px-3 sm:pb-3">{children}</div>

      {/*
        图钉清单放在浅色底上：PinList / PinDetail 是按浅色界面做的，
        直接丢进深底里文字会糊。给它独立一块，顺便划出「看图」和「读批注」的分界。
      */}
      {aside && (
        <div className="safe-bottom max-h-[38vh] shrink-0 overflow-auto border-t border-rule bg-paper px-4 py-3">
          {aside}
        </div>
      )}
    </div>
  );
}
